'use client';

import { Add as AddIcon, Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import {
	Button,
	IconButton,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { useLazyQuery } from '@parkchirp/api-client';
import orderBy from 'lodash/orderBy';
import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';

import { GET_DELETE_SIGNED_URL, GET_PRESIGNED_URL, GET_PUT_SIGNED_URL } from '../../queries';
import { SnackbarParams } from '../../types';
import { addFileUploadTableFiles, dayjs, FileUploadTableFile, isOwnerPortal } from '../../utils';

interface FileUploadTableProps {
	bucket: string;
	bucketDir: string;
	files: FileUploadTableFile[];
	accept?: string;
	includeTimestamp?: boolean;
	includeDateColumn?: boolean;
	uploadDisabled?: boolean;
	uploadButtonTitle?: string;
	externalUploadingFiles?: string[]; // Files added using a button external from this component
	setFiles: (files: FileUploadTableFile[]) => void | Promise<void>;
	handleDeleteFile: (file: FileUploadTableFile) => void;
	useAppContext: () => {
		setShowSnackbar: Dispatch<SetStateAction<Partial<SnackbarParams> | null>>;
	};
	readOnly?: boolean;
	getPresignedUrlAddEnv?: boolean;
}

export const FileUploadTable = ({
	bucket,
	bucketDir,
	files = [],
	accept = '.pdf,.csv,.xlsx',
	includeTimestamp = false,
	includeDateColumn = true,
	uploadDisabled = false,
	uploadButtonTitle = 'Add Document',
	externalUploadingFiles = [],
	setFiles,
	handleDeleteFile,
	useAppContext,
	readOnly = isOwnerPortal,
	getPresignedUrlAddEnv = true,
}: FileUploadTableProps) => {
	const { setShowSnackbar } = useAppContext();

	// Consumers may persist files and replace temporary rows with database records.
	// Keep their array callback contract and use the parent's latest authoritative list.
	const filesRef = useRef(files);
	filesRef.current = files;
	const updateFiles = async (update: SetStateAction<FileUploadTableFile[]>) => {
		const nextFiles = typeof update === 'function' ? update(filesRef.current) : update;
		await setFiles(nextFiles);
	};

	const operationInProgress = useRef(false);
	const [busy, setBusy] = useState(false);
	const beginOperation = () => {
		if (operationInProgress.current || externalUploadingFiles.length) {
			return false;
		}
		operationInProgress.current = true;
		setBusy(true);
		return true;
	};
	const endOperation = () => {
		operationInProgress.current = false;
		setBusy(false);
	};

	const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
	const [fetchingUrl, setFetchingUrl] = useState<boolean>(false);

	const [getPutSignedUrl] = useLazyQuery(GET_PUT_SIGNED_URL);
	const [getDeleteSignedUrl] = useLazyQuery(GET_DELETE_SIGNED_URL);
	const [getPresignedUrl] = useLazyQuery(GET_PRESIGNED_URL);

	const fetchFileUrl = async (bucketPath: string, fileName: string) => {
		setFetchingUrl(true);
		const s3Details = bucketPath.split('/');

		const presignedUrl = await getPresignedUrl({
			variables: {
				// Get the bucket name from the first element in the array
				bucket: s3Details.shift(),
				// The remainder of s3Details is the s3 key
				s3Key: `${s3Details.join('/')}/${fileName}`,
				addEnv: getPresignedUrlAddEnv,
			},
			context: {
				apiVersion: 'v2',
			},
		});

		setFetchingUrl(false);

		return presignedUrl.data.getPresignedUrl.presignedUrl;
	};

	const onDownloadFile = async (bucketPath: string, fileName: string) => {
		const fileUrl = await fetchFileUrl(bucketPath, fileName);

		window.open(fileUrl, 'blank');
	};

	const orderedByDateFiles = useMemo(() => orderBy(files, ['createdAt'], ['desc']), [files]);
	const inProgressFileNames = useMemo(
		() =>
			[...uploadingFiles, ...externalUploadingFiles].filter(
				(fileName) => !files.some((file) => file.fileName === fileName),
			),
		[externalUploadingFiles, files, uploadingFiles],
	);

	const onDeleteFile = async (file) => {
		if (!beginOperation()) {
			return;
		}
		try {
			const deleteSignedUrlResponse = await getDeleteSignedUrl({
				variables: {
					bucket,
					s3Key: `${bucketDir}/${file.fileName}`,
				},
				context: {
					apiVersion: 'v2',
				},
			});

			const deleteSignedUrl = deleteSignedUrlResponse.data.getDeleteSignedUrl.url;

			const response = await fetch(deleteSignedUrl, { method: 'DELETE' });
			if (!response.ok) {
				throw new Error(`Delete failed with status ${response.status}`);
			}

			await updateFiles((currentFiles) => currentFiles.filter(({ id }) => id !== file.id));
			await handleDeleteFile(file);
		} catch (error) {
			setShowSnackbar({
				message: `Unable to delete file: ${error.message}`,
				severity: 'error',
			});
		} finally {
			endOperation();
		}
	};

	return (
		<TableContainer component={Paper}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell align='left' sx={{ width: '220px' }}>
							{!readOnly ? (
								<Button
									component='label'
									sx={{ height: '100%' }}
									disabled={uploadDisabled || busy || !!externalUploadingFiles.length}
								>
									<AddIcon sx={{ marginRight: '5px' }} />
									{uploadButtonTitle}
									<input
										type='file'
										accept={accept}
										onChange={async (e) => {
											if (!beginOperation()) {
												return;
											}
											const input = e.currentTarget;
											try {
												await addFileUploadTableFiles(
													e.target.files,
													files,
													bucket,
													bucketDir,
													includeTimestamp,
													getPutSignedUrl,
													setUploadingFiles,
													updateFiles,
													setShowSnackbar,
												);
											} finally {
												input.value = '';
												endOperation();
											}
										}}
										multiple
										hidden
									/>
								</Button>
							) : (
								<span />
							)}
						</TableCell>
						{includeDateColumn && (
							<TableCell align='left' className='black-text' sx={{ width: '250px' }}>
								Date
							</TableCell>
						)}
						<TableCell align='left' className='black-text'>
							File Name
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{inProgressFileNames.map((fileName) => (
						<TableRow key={`uploading-${fileName}`}>
							<TableCell />
							{includeDateColumn && <TableCell />}
							<TableCell>
								<div className='black-text'>
									{fileName}
									<LinearProgress sx={{ width: '50%' }} />
								</div>
							</TableCell>
						</TableRow>
					))}
					{orderedByDateFiles.map((file) => (
						<TableRow key={file.id}>
							<TableCell>
								{!readOnly && (
									<IconButton
										onClick={() => onDeleteFile(file)}
										disabled={fetchingUrl || busy || !!externalUploadingFiles.length}
										style={{ color: 'black' }}
									>
										<DeleteIcon />
									</IconButton>
								)}
								{file.bucketPath && (
									<IconButton
										onClick={() => onDownloadFile(file.bucketPath, file.fileName)}
										disabled={fetchingUrl}
										style={{ color: 'black' }}
									>
										<DownloadIcon />
									</IconButton>
								)}
							</TableCell>
							{includeDateColumn && (
								<TableCell className='black-text'>
									{dayjs(file.createdAt).format('YYYY-MM-DD HH:mm:ss')}
								</TableCell>
							)}
							<TableCell>
								<div className='black-text'>{file.fileName}</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
